import React from 'react';
import { Table } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import css from './SortableTable.scss';
import PaginationBar from './PaginationBar';
import SortableHeader from './SortableHeader';
import { TableHeaderCell } from 'utils/types';

export default class SortableTable extends React.Component {

  /**
   * If no item renderer is provided, this function will render a table row for the given item. For every header,
   * a table cell will be added with the corresponding data points.
   * @param {Object} item - the item we are rendering a table row for
   * @param {number} index - the item index
   * @returns {XML} the table row for the given item
   */
  renderItemDefault(item, index) {
    return <Table.Row key={index}>
      {this.props.headers.map((header, hIndex) => <Table.Cell key={hIndex}>{item[header.sortValue]}</Table.Cell>
      )}
    </Table.Row>
  }
  
  get tableClasses() {
    const classes = [ css['sf-table'] ];
    if (this.props.className) classes.push(this.props.className);
    return classes.join(' ');
  }

  // test to display when there are no rows of data to render
  get emptyText() {
    return <div className={css['empty-text']} aria-live="polite">No {this.props.itemName.toLowerCase()} to display</div>
  }

  get header() {
    return <SortableHeader columnToSort={this.props.columnToSort}
                           headers={this.props.headers}
                           sortDirection={this.props.sortDirection}
                           onSortChange={this.props.onSortChange} 
                           fixed={this.props.fixed} />
  }

  // if shouldPaginate is true, add pagination into the footer
  get footer() {
    return this.props.shouldPaginate ? this.pagination : null;
  }

  // if an item renderer has been provided, use it. Otherwise, use the default item renderer
  get renderItem() {
    return this.props.renderItem || this.renderItemDefault.bind(this);
  }

  get pagination() {
    return <PaginationBar colspan={this.props.headers.length}
                          itemName={this.props.itemName}
                          itemsPerPage={this.props.itemsPerPage}
                          onPageChange={this.props.onPageChange}
                          start={this.props.start}
                          totalItems={this.props.totalItems}
                          totalSelected={this.props.totalSelected}/>
  }

  render() {
    if (this.props.items.length === 0) return this.emptyText;
    const { unstackable, fixed, celled} = this.props;
    return <Table unstackable={unstackable}
                  className={this.tableClasses}
                  role="grid"
                  fixed={fixed}
                  sortable
                  striped
                  selectable
                  celled={celled}>
      <Table.Header>{this.header}</Table.Header>
      <Table.Body>{this.props.items.map(this.renderItem)}</Table.Body>
      <Table.Footer>{this.footer}</Table.Footer>
    </Table>
  }
}

SortableTable.defaultProps = {
  itemName: 'Items',
  itemsPerPage: 25,
  sortDirection: 'ASC',
  start: 0,
  fixed: false,
  shouldPaginate: true,
  unstackable: true,
  celled: false
};

SortableTable.propTypes = {
  className: PropTypes.string,
  columnToSort: PropTypes.string.isRequired,
  headers: PropTypes.arrayOf(TableHeaderCell).isRequired,
  itemName: PropTypes.string,
  items: PropTypes.array.isRequired,
  itemsPerPage: PropTypes.number,
  onPageChange: PropTypes.func,
  onSortChange: PropTypes.func,
  renderItem: PropTypes.func,
  fixed: PropTypes.bool,
  shouldPaginate: PropTypes.bool,
  sortDirection: PropTypes.string,
  start: PropTypes.number,
  totalItems: PropTypes.number,
  totalSelected: PropTypes.number,
  unstackable: PropTypes.bool,
  celled: PropTypes.bool
};
